import { Button, Loader, Avatar, Container, Menu, Modal, TextInput, Stack, Text, Group } from '@mantine/core';
import { IconLogout, IconBrandWindows, IconMail, IconSend, IconCircleCheck } from '@tabler/icons-react';
import { useMsal } from '@azure/msal-react';
import { useState } from 'react';
import { loginRequest } from '../authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { clearMagicLinkSession, hasMagicLinkSession } from '../helpers/TokenRefresh';
import HttpClient from '../helpers/HttpClient';
import { notifications } from '@mantine/notifications';
import { useUserContext } from '../providers/UserContext';

export const Login = () => {
  const { instance, accounts } = useMsal();
  const { currentPerson, refreshUserRights } = useUserContext();
  const [loading, setLoading] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const isMagicLinkSession = hasMagicLinkSession();
  const isLoggedIn = accounts.length > 0 || isMagicLinkSession;

  const handleLogin = async () => {
    setLoading(true);
    if (accounts.length > 0) {
      try {
        await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        setLoading(false);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.loginRedirect(loginRequest);
        } else {
          setLoading(false);
        }
      }
    } else {
      instance.loginRedirect(loginRequest).catch(() => setLoading(false));
    }
  };

  const handleLogout = () => {
    if (isMagicLinkSession) {
      clearMagicLinkSession();
      refreshUserRights();
    } else {
      instance.logoutRedirect();
    }
  };

  const closeEmailModal = () => {
    setEmailModalOpen(false);
    setEmailSent(false);
    setEmail('');
  };

  const requestMagicLink = async () => {
    setEmailSending(true);
    try {
      await new HttpClient('/auth/magic-link').postAction('request', { email });
      setEmailSent(true);
    } catch {
      notifications.show({ message: 'Anfrage fehlgeschlagen', color: 'red' });
    } finally {
      setEmailSending(false);
    }
  };

  const displayName = accounts[0]?.name ?? (currentPerson ? `${currentPerson.firstName} ${currentPerson.lastName}` : '');

  return (
    <>
      <Container fluid>
        {loading ? (
          <Loader size="md" />
        ) : isLoggedIn ? (
          <Menu shadow="md" width={160}>
            <Menu.Target>
              <Avatar
                radius="xl"
                src={accounts[0] ? `https://ui-avatars.com/api/?name=${accounts[0].name}` : `https://ui-avatars.com/api/?name=${displayName}`}
                style={{ cursor: 'pointer' }}
              />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{displayName}</Menu.Label>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={14} />}
                onClick={handleLogout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <Button
              size="sm"
              leftSection={<IconBrandWindows size={16} />}
              onClick={handleLogin}
            >
              Sign in with Microsoft
            </Button>
            <Button
              size="sm"
              variant="default"
              leftSection={<IconMail size={16} />}
              onClick={() => setEmailModalOpen(true)}
            >
              Mit E-Mail anmelden
            </Button>
          </Group>
        )}
      </Container>

      <Modal opened={emailModalOpen} onClose={closeEmailModal} title="Mit E-Mail anmelden" centered radius="md">
        {emailSent ? (
          <Stack align="center" gap="xs" py="sm">
            <IconCircleCheck size={40} color="var(--mantine-color-teal-6)" />
            <Text size="sm" ta="center">
              Falls diese E-Mail-Adresse registriert ist, wurde ein Login-Link gesendet. Bitte prüfen Sie Ihr Postfach.
            </Text>
          </Stack>
        ) : (
          <Stack>
            <TextInput
              label="E-Mail-Adresse"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && email && requestMagicLink()}
              autoFocus
            />
            <Button
              fullWidth
              leftSection={<IconSend size={16} />}
              onClick={requestMagicLink}
              loading={emailSending}
              disabled={!email}
            >
              Link anfordern
            </Button>
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default Login;
